import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import postService, { Post } from '../services/postService';
import mediaService, { MediaFile } from '../services/mediaService';
import AuthenticatedImage from '../components/media/AuthenticatedImage';
import { useClient } from '../contexts/ClientContext';

const PostDetailPage: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentClient } = useClient();

  useEffect(() => {
    const fetchPost = async () => {
      if (!postId) return;
      setIsLoading(true);
      try {
        const fetchedPost = await postService.getPost(postId);
        setPost(fetchedPost);

        if (fetchedPost.media_files && fetchedPost.media_files.length > 0 && currentClient) {
          const fetchedMediaFiles = await Promise.all(
            fetchedPost.media_files.map(fileId => mediaService.getMediaFile(fileId, currentClient.id))
          );
          setMediaFiles(fetchedMediaFiles);
        }
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to fetch post');
        console.error('Failed to fetch post:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPost();
  }, [postId, currentClient]);

  if (isLoading) {
    return <div className="text-center py-8">Loading post...</div>;
  }

  if (error) {
    return <div className="text-center py-8 text-red-600">Error: {error}</div>;
  }

  if (!post) {
    return <div className="text-center py-8">Post not found.</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-3xl font-bold mb-4">{post.title}</h1>
      <p className="text-gray-700 mb-6">{post.content}</p>

      {mediaFiles.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {mediaFiles.map(file => (
            <div key={file.id} className="relative group aspect-w-16 aspect-h-9">
              {currentClient && (
                <AuthenticatedImage 
                  src={file.file_url} 
                  alt={file.original_filename} 
                  className="rounded-lg object-cover w-full h-full" 
                  clientId={currentClient.id}
                />
              )}
            </div>
          ))}
        </div>
      )}

      <div className="text-sm text-gray-500">
        <p>Status: <span className="capitalize">{post.status}</span></p>
        {post.scheduled_at && <p>Scheduled: {new Date(post.scheduled_at).toLocaleString()}</p>}
        {post.published_at && <p>Published: {new Date(post.published_at).toLocaleString()}</p>}
        <p>Created: {new Date(post.created_at).toLocaleString()}</p>
        <p>Last Updated: {new Date(post.updated_at).toLocaleString()}</p>
      </div>
    </div>
  );
};

export default PostDetailPage;
